
import {
	useEffect,
	useMemo,
	useState
} from 'react';

import {
	UseInfiniteQueryResult
} from 'react-query';

import Constants from 'components/WorkZone/Odata/Constants';

import { ODataAccept, useInifiniteOdata, useOdata } from 'components/WorkZone/Odata/Odata';

import GetDefaultLayout, { Customizable, ListLayout } from 'components/Table/Layout';

export type TableSourceOptions = {
	collection: string,
	filter?: any
}

interface TableSourceInstance {

	getProperties(): string[]

	options: TableSourceOptions,

	collection: string
}

const DEFAULT_PROPERTIES = ['ID', 'Summary'];

export default class TableSource implements TableSourceInstance {

	constructor(options: TableSourceOptions, customLayout: { [collection: string]: ListLayout } | undefined) {
		this.options = options;
		this.customLayout = customLayout;
	}

	public options;
	public customLayout;

	get collection() {
		return this.options.collection;
	}

	update(options: TableSourceOptions) {
		this.options = options;
	}

	getProperties() {

		const layout = this.getLayout();

		if (layout) {
			return [...new Set([
				...DEFAULT_PROPERTIES,
				...Object.keys(layout.columns).filter(column => layout.columns[column].exists !== false),
				...layout.data || []
			])];
		}

		return DEFAULT_PROPERTIES;
	}

	getLayout() {
		const defaultLayoutForCollection = GetDefaultLayout()[this.options.collection] || null;
		if(this.customLayout && defaultLayoutForCollection){
			const customLayoutForCollection = this.customLayout[this.options.collection];
			const constantColumns = Object.entries(defaultLayoutForCollection.columns).filter(entry => entry[1].customizable === Customizable.readonly);
			const customColumns = Object.entries(customLayoutForCollection.columns);
			customColumns.push(...constantColumns);

			const mergedLayout = defaultLayoutForCollection;
			mergedLayout.columns = Object.fromEntries(customColumns);

			return mergedLayout;
		}
		return defaultLayoutForCollection;
	}
}

export type UseInfiniteSource = UseInfiniteQueryResult<any, unknown> & { source: TableSource, count: number, key: string };

export function useInfiniteSource(options: any, customLayout: { [collection: string]: ListLayout } | undefined): UseInfiniteSource {

	const [shouldCount, startCounting] = useState(false);

	const [useCount, setUseCount] = useState<number | null>(null);

	const source = useMemo(() => new TableSource(options, customLayout), [customLayout, options]);

	const properties = source.getProperties();

	const query = useInifiniteOdata({
		[source.options.collection]: {
			select: properties,
			filter: source.options.filter
		}
	});

	const { data, refetch, isLoading, isFetching } = query;

	useEffect(() => {

		if (!isLoading && !isFetching) {

			if (data) {

				if (data.pages && data.pages[0][Constants.keys.nextLink]) {
					startCounting(true);
				} else {
					setUseCount(data.pages[0]?.value?.length || 0);
				}
			} else {
				setUseCount(0);
			}
		}

	}, [data, isFetching, isLoading]);




	const count = useCountingSource(options, customLayout, shouldCount, useCount);

	const refetchWrapper = async () => {

		startCounting(false);

		return refetch({
			cancelRefetch: true
		});
	};

	return { ...query, source, count, refetch: refetchWrapper };
}

function useCountingSource(options: any, customLayout: { [collection: string]: ListLayout } | undefined, shouldCount = false, useCount: number | null = null) {

	const [count, setCount] = useState(0);

	const source = useMemo(() => new TableSource(options, customLayout), [customLayout, options]);

	const { data, isLoading, isFetching } = useOdata(
		{
			[`${source.options.collection}/$count`]: {
				filter: source.options.filter
			}
		},
		{
			accept: ODataAccept.Text,
			enabled: shouldCount
		}
	);

	useEffect(() => {
		if (!isLoading && !isFetching && data) {
			setCount(Number(data) || 0);
		}
	}, [data, isFetching, isLoading]);

	useEffect(() => {

		if (useCount !== null) {
			setCount(Number(useCount));
		}

	}, [useCount]);

	return count;
}
